import React, {type ComponentClass, type FunctionComponent} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {withRouter, type WithRouterProps} from 'react-router';

import {TextHighlightsContextProvider} from 'app/comments/TextHighlightsContextProvider';
import {DetailPageContextProvider} from 'app/components/general/DetailPage/context/DetailPageContextProvider';

import type {
  CurrentItemType,
  CurrentVersionListType,
  CurrentVersionType,
  DetailPageItemType,
  DetailPageRouteProps,
  DetailPageRouterProps,
  EditPageBaseProps,
} from 'app/components/general/DetailPage/types';
import type {
  UseListQueryType,
  UseQueryType,
} from 'app/store/sweet-api/api-query';

export interface GenericDocumentPropsForEditPage<
  ItemType extends DetailPageItemType
> {
  object: CurrentItemType<ItemType>;
  version: CurrentVersionType<ItemType>;
  refetch: () => void;
}

type RenderWhenDocumentIsGatheredEditPageProps<
  ItemType extends DetailPageItemType
> = {
  useHashidHook: UseQueryType<string, CurrentItemType<ItemType>>;
  useSelectedVersionHook: UseQueryType<
    {versionId: string | number},
    CurrentVersionType<ItemType>
  >;
  DetailPage:
    | FunctionComponent<GenericDocumentPropsForEditPage<ItemType>>
    | ComponentClass<
        Omit<GenericDocumentPropsForEditPage<ItemType>, keyof WithRouterProps>
      >;
};

function BaseRenderWhenDocumentIsGatheredEditPage<
  ItemType extends DetailPageItemType
>({
  useHashidHook,
  useSelectedVersionHook,
  params,
  DetailPage,
  location,
}: EditPageBaseProps & RenderWhenDocumentIsGatheredEditPageProps<ItemType>) {
  const {versionid: versionId, hashid: hashId} = params;

  const {data: object, refetch} = useHashidHook(hashId);
  const {data: version} = useSelectedVersionHook(
    {versionId},
    {refetchOnMountOrArgChange: false}
  );

  if (object == null || version == null) {
    return null;
  } else
    return (
      <DetailPageContextProvider threadId={location.query.threadid}>
        <TextHighlightsContextProvider contentType={object.content_type}>
          <DetailPage object={object} version={version} refetch={refetch} />
        </TextHighlightsContextProvider>
      </DetailPageContextProvider>
    );
}

export const RenderWhenDocumentIsGatheredEditPage = withRouter<
  RenderWhenDocumentIsGatheredEditPageProps<DetailPageItemType>
>(BaseRenderWhenDocumentIsGatheredEditPage);

export interface GenericDocumentPropsForDetailPage<
  ItemType extends DetailPageItemType
> {
  route: {
    props?: DetailPageRouteProps<ItemType>;
  };
  object: CurrentItemType<ItemType>;
  version: CurrentVersionType<ItemType> | undefined;
  versions: Array<CurrentVersionListType<ItemType>>;
  refetch: () => void;
  refetchVersion: () => void;
  refetchVersions: () => void;
}

type RenderWhenDocumentIsGatheredDetailPageProps<
  ItemType extends DetailPageItemType
> = {
  route: {
    props?: DetailPageRouteProps<ItemType>;
  };
  useHashidHook: UseQueryType<string, CurrentItemType<ItemType>>;
  useSelectedVersionHook: UseQueryType<
    {versionId: string | number},
    CurrentVersionType<ItemType>
  >;
  useVersionsHook: UseListQueryType<
    {
      objectId?: string | number;
      page?: string | number;
      page_size?: string;
    },
    CurrentVersionListType<ItemType>
  >;
  DetailPage:
    | FunctionComponent<GenericDocumentPropsForDetailPage<ItemType>>
    | ComponentClass<
        Omit<
          GenericDocumentPropsForDetailPage<ItemType>,
          keyof DetailPageRouterProps
        >
      >;
};

function BaseRenderWhenDocumentIsGatheredDetailPage<
  ItemType extends DetailPageItemType
>({
  route,
  useHashidHook,
  useSelectedVersionHook,
  useVersionsHook,
  params,
  DetailPage,
}: DetailPageRouterProps &
  RenderWhenDocumentIsGatheredDetailPageProps<ItemType>) {
  const {versionid: versionId, hashid: hashId} = params;

  const {data: object, refetch} = useHashidHook(hashId);
  const {data: version, refetch: refetchVersion} = useSelectedVersionHook(
    versionId ? {versionId} : skipToken,
    {
      selectFromResult: ({data, status}) =>
        status !== 'uninitialized' ? {data} : {data: undefined},
      refetchOnMountOrArgChange: false,
    }
  );

  const {data: versions, refetch: refetchVersions} = useVersionsHook(
    object
      ? {
          objectId: object.id,
        }
      : skipToken
  );

  if (object == null || versions == null) {
    return null;
  } else
    return (
      <TextHighlightsContextProvider contentType={object.content_type}>
        <DetailPage
          route={route}
          object={object}
          version={version}
          versions={versions.results}
          refetch={refetch}
          refetchVersion={refetchVersion}
          refetchVersions={refetchVersions}
        />
      </TextHighlightsContextProvider>
    );
}

export const RenderWhenDocumentIsGatheredDetailPage = withRouter<
  RenderWhenDocumentIsGatheredDetailPageProps<DetailPageItemType>
>(BaseRenderWhenDocumentIsGatheredDetailPage);
